// LocalStorage-backed shopping cart for MastiBazaar.
// Lines are keyed by product id + size + color so the same product in two
// sizes (or two colors) shows up as separate cart entries.
const CART_KEY = "mb_cart_v1";
const CART_EVENT = "mb-cart-change";

const safeRead = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeWrite = (items) => {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota errors */
  }
  try {
    window.dispatchEvent(new Event(CART_EVENT));
  } catch {
    /* noop */
  }
};

export const CART_CHANGE_EVENT = CART_EVENT;

export const lineKey = (productId, size, color) =>
  `${productId}::${size || "Free Size"}::${color || "default"}`;

export const getCart = () => safeRead();

export const cartCount = (items = getCart()) =>
  items.reduce((acc, i) => acc + (Number(i.qty) || 0), 0);

export const cartSubtotal = (items = getCart()) =>
  items.reduce(
    (acc, i) => acc + (Number(i.price) || 0) * (Number(i.qty) || 0),
    0,
  );

// Adds a variant. `variant = { size, color, colorLabel }` — color is the
// variant image URL; colorLabel is the human-readable name ("Variant 1").
export const addToCart = (product, variant = {}, qty = 1) => {
  const items = safeRead();
  const size = variant.size || "Free Size";
  const color = variant.color || null;
  const colorLabel = variant.colorLabel || null;
  const key = lineKey(product.id, size, color);

  const idx = items.findIndex((i) => i.key === key);
  if (idx >= 0) {
    items[idx] = { ...items[idx], qty: items[idx].qty + qty };
  } else {
    items.unshift({
      key,
      id: product.id,
      title: product.title,
      price: product.price,
      // Cart line preview thumbnail: prefer the selected color image, else
      // fall back to the product's main image.
      image: color || product.image,
      category: product.category,
      size,
      color,
      colorLabel,
      qty,
    });
  }
  safeWrite(items);
  return items;
};

export const updateQty = (key, qty) => {
  const items = safeRead()
    .map((i) => (i.key === key ? { ...i, qty: Math.max(0, qty) } : i))
    .filter((i) => i.qty > 0);
  safeWrite(items);
  return items;
};

export const removeFromCart = (key) => {
  const items = safeRead().filter((i) => i.key !== key);
  safeWrite(items);
  return items;
};

export const clearCart = () => {
  safeWrite([]);
};
